import {
  BackupPolicyType,
  Provider,
  type BackupPolicyConfig,
  type InventoryBackupPolicy,
} from '@repo/api-gw-sdk';

export const MIN_RETENTION_DAYS_FOR_NATIVE = 1;

export const isPitrPolicyType = (policyType?: BackupPolicyType) => {
  return (
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR ||
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR ||
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR ||
    policyType ===
      BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_SQL_DATABASE_NATIVE_PITR
  );
};

export const isStandardPolicyType = (policyType?: BackupPolicyType) => {
  return (
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD ||
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_STANDARD ||
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES
  );
};

export const isHighFrequencyPolicyType = (policyType?: BackupPolicyType) => {
  return (
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY ||
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_HIGH_FREQUENCY
  );
};

// Policies whose snapshots stay in the AWS source account: each schedule targets a region instead of a vault.
export const isAwsSourceAccountPolicyType = (policyType?: BackupPolicyType) => {
  return (
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_STANDARD ||
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_HIGH_FREQUENCY
  );
};

export const isNativePolicyType = (policyType?: BackupPolicyType) => {
  return (
    isAwsSourceAccountPolicyType(policyType) ||
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR ||
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR ||
    policyType ===
      BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_SQL_DATABASE_NATIVE_PITR ||
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES
  );
};

export const isGoogleWorkspacePolicyType = (policyType?: BackupPolicyType) => {
  return policyType === BackupPolicyType.BACKUP_POLICY_TYPE_GOOGLE_WORKSPACE;
};

export const isMicrosoft365PolicyType = (policyType?: BackupPolicyType) => {
  return policyType === BackupPolicyType.BACKUP_POLICY_TYPE_MICROSOFT_365;
};

export const isAzureEntraIdPolicyType = (policyType?: BackupPolicyType) => {
  return policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_ENTRA_ID;
};

// Microsoft 365 and Entra ID both offer the unlimited-retention sentinel
// (36500 days); other policy types don't.
export const supportsUnlimitedRetention = (policyType?: BackupPolicyType) => {
  return (
    isMicrosoft365PolicyType(policyType) || isAzureEntraIdPolicyType(policyType)
  );
};

// SaaS workspace policies (Google Workspace, Microsoft 365) share a single-
// resource, fixed-schedule shape and the same simplified card/flow behavior.
export const isSaasWorkspacePolicyType = (policyType?: BackupPolicyType) => {
  return (
    isGoogleWorkspacePolicyType(policyType) ||
    isMicrosoft365PolicyType(policyType)
  );
};

// Single-tenant SaaS policies (one policy per workspace/tenant, enforced by the
// backend overlap validation). Gates the card's Duplicate action and inventory
// link. Distinct from isSaasWorkspacePolicyType: Entra ID is single-tenant but
// keeps the multi-schedule, user-chosen-cadence editor.
export const isSingleTenantSaasPolicyType = (policyType?: BackupPolicyType) => {
  return (
    isSaasWorkspacePolicyType(policyType) ||
    isAzureEntraIdPolicyType(policyType)
  );
};

export const isContinuousPolicyType = (policyType?: BackupPolicyType) => {
  return (
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR ||
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR ||
    policyType ===
      BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_SQL_DATABASE_NATIVE_PITR
  );
};

// backupPolicyNativeProvider resolves the cloud provider used for the
// account-as-vault representation on native policy cards. Each native
// policyType implies a single provider, so the mapping is direct.
export const backupPolicyNativeProvider = (
  policy: BackupPolicyConfig | InventoryBackupPolicy
): Provider => {
  switch (policy.policyType) {
    case BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES:
    case BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_SQL_DATABASE_NATIVE_PITR:
      return Provider.Azure;
    case BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR:
      return Provider.Gcp;
    default:
      return Provider.Aws;
  }
};
